import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Activity, AlertTriangle, CheckCircle2, RefreshCw, Search, FileWarning } from 'lucide-react';
import { diagnosticsAPI } from '../lib/api';

/**
 * P_UNIFIED_VALUE_ENGINE_1.0 Phase 5 — observability page.
 * Pure data fan-out from existing read-only endpoints. No math
 * happens here; every dollar / count is sourced verbatim from the
 * server, which delegates to services.value_engine.compute_value.
 */
const Stat = ({ label, value, hint, testid }) => (
    <div className="space-y-1" data-testid={testid}>
        <div className="text-xs text-slate-500 uppercase tracking-wide">{label}</div>
        <div className="text-2xl font-semibold text-slate-900">{value}</div>
        {hint ? <div className="text-xs text-slate-500">{hint}</div> : null}
    </div>
);

const healthBadge = (status, t) => {
    if (status === 'Healthy') {
        return <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200" data-testid="ves-health-badge">{t('diagnostics.health.healthy')}</Badge>;
    }
    if (status === 'Warning') {
        return <Badge className="bg-amber-100 text-amber-700 border-amber-200" data-testid="ves-health-badge">{t('diagnostics.health.warning')}</Badge>;
    }
    return <Badge className="bg-rose-100 text-rose-700 border-rose-200" data-testid="ves-health-badge">{t('diagnostics.health.investigate')}</Badge>;
};

const orphanColor = (n) => {
    if (n === 0) return 'text-emerald-700';
    if (n <= 10) return 'text-amber-700';
    return 'text-rose-700';
};

const AdminDiagnostics = () => {
    const { t } = useTranslation();
    const [status, setStatus] = useState(null);
    const [orphans, setOrphans] = useState(null);
    const [audit, setAudit] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const reload = async () => {
        setLoading(true);
        setError(null);
        try {
            const [s, o, a] = await Promise.all([
                diagnosticsAPI.valueEngineStatus(),
                diagnosticsAPI.orphanTaps(),
                diagnosticsAPI.snapshotAudit(),
            ]);
            setStatus(s.data);
            setOrphans(o.data);
            setAudit(a.data);
        } catch (e) {
            setError(e.response?.data?.detail || e.message || 'load_failed');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { reload(); }, []);

    const reasonCounts = (audit?.items || []).reduce((acc, r) => {
        const k = r.reason || 'unknown';
        acc[k] = (acc[k] || 0) + 1;
        return acc;
    }, {});

    return (
        <div className="p-8 max-w-7xl mx-auto space-y-8" data-testid="admin-diagnostics-page">
            <header className="flex items-start justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-semibold text-slate-900" data-testid="diagnostics-page-title">
                        {t('diagnostics.title')}
                    </h1>
                    <p className="text-sm text-slate-600 mt-1 max-w-2xl">{t('diagnostics.subtitle')}</p>
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={reload}
                    disabled={loading}
                    data-testid="diagnostics-reload-btn"
                >
                    <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                    {t('diagnostics.reload')}
                </Button>
            </header>

            {error ? (
                <Card className="p-6 border-rose-200 bg-rose-50 text-rose-700" data-testid="diagnostics-error">
                    {t('diagnostics.error')}: {String(error)}
                </Card>
            ) : null}

            {/* Value Engine Status */}
            <Card className="p-6 space-y-4" data-testid="card-value-engine-status">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Activity className="w-5 h-5 text-slate-500" />
                        <h2 className="text-lg font-semibold text-slate-900">{t('diagnostics.ves.title')}</h2>
                    </div>
                    {status ? healthBadge(status.health, t) : null}
                </div>
                <p className="text-xs text-slate-500">{t('diagnostics.ves.description')}</p>
                {status ? (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6 pt-2">
                        <Stat
                            label={t('diagnostics.ves.engineVersion')}
                            value={`v${status.engine_version}`}
                            testid="ves-engine-version"
                        />
                        <Stat
                            label={t('diagnostics.ves.pricingSource')}
                            value={status.pricing_source}
                            testid="ves-pricing-source"
                        />
                        <Stat
                            label={t('diagnostics.ves.pricingVersion')}
                            value={`v${status.pricing_version}`}
                            testid="ves-pricing-version"
                        />
                        <Stat
                            label={t('diagnostics.ves.activePricingRows')}
                            value={`${status.active_platform_values.org_specific} / ${status.active_platform_values.global_default}`}
                            hint={t('diagnostics.ves.activePricingHint')}
                            testid="ves-active-pricing"
                        />
                        <Stat
                            label={t('diagnostics.ves.activeOrgs')}
                            value={status.active_organizations}
                            testid="ves-active-orgs"
                        />
                        <Stat
                            label={t('diagnostics.ves.lastSnapshot')}
                            value={status.last_snapshot?.week_end || '—'}
                            hint={status.last_snapshot?.created_at?.slice(0, 10) || ''}
                            testid="ves-last-snapshot"
                        />
                    </div>
                ) : null}
            </Card>

            {/* Orphan Taps */}
            <Card className="p-6 space-y-4" data-testid="card-orphan-taps">
                <div className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-slate-500" />
                    <h2 className="text-lg font-semibold text-slate-900">{t('diagnostics.orphan.title')}</h2>
                </div>
                <p className="text-xs text-slate-500">{t('diagnostics.orphan.description')}</p>
                {orphans ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-6 pt-2">
                        <div>
                            <div className="text-xs text-slate-500 uppercase tracking-wide">{t('diagnostics.orphan.count')}</div>
                            <div className={`text-4xl font-semibold mt-1 ${orphanColor(orphans.orphan_total)}`} data-testid="orphan-count">
                                {orphans.orphan_total}
                            </div>
                            <div className="text-xs text-slate-500 mt-1">
                                {t('diagnostics.orphan.threshold')}
                            </div>
                        </div>
                        <Stat
                            label={t('diagnostics.orphan.totalTaps')}
                            value={orphans.total_taps}
                            testid="orphan-total-taps"
                        />
                        <Stat
                            label={t('diagnostics.orphan.affectedOrgs')}
                            value={orphans.by_organization?.length || 0}
                            testid="orphan-affected-orgs"
                        />
                    </div>
                ) : null}
                {orphans?.by_organization?.length ? (
                    <div className="border-t border-slate-100 pt-3 text-sm">
                        {orphans.by_organization.map(row => (
                            <div key={row.organization_id || 'none'} className="flex justify-between py-1" data-testid={`orphan-org-row-${row.organization_id || 'none'}`}>
                                <span className="text-slate-700">{row.organization_name}</span>
                                <span className="text-slate-500">{row.orphan_tap_count}</span>
                            </div>
                        ))}
                    </div>
                ) : null}
            </Card>

            {/* Snapshot Audit Summary */}
            <Card className="p-6 space-y-4" data-testid="card-snapshot-audit">
                <div className="flex items-center gap-2">
                    <FileWarning className="w-5 h-5 text-slate-500" />
                    <h2 className="text-lg font-semibold text-slate-900">{t('diagnostics.audit.title')}</h2>
                </div>
                <p className="text-xs text-slate-500">{t('diagnostics.audit.description')}</p>
                {audit ? (
                    <>
                        <div className="grid grid-cols-3 gap-6 pt-2">
                            <div>
                                <div className="flex items-center gap-2">
                                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                                    <span className="text-xs text-slate-500 uppercase tracking-wide">{t('diagnostics.audit.healthy')}</span>
                                </div>
                                <div className="text-3xl font-semibold text-emerald-700 mt-1" data-testid="audit-healthy">{audit.summary.HEALTHY}</div>
                            </div>
                            <div>
                                <div className="flex items-center gap-2">
                                    <AlertTriangle className="w-4 h-4 text-amber-600" />
                                    <span className="text-xs text-slate-500 uppercase tracking-wide">{t('diagnostics.audit.legacy')}</span>
                                </div>
                                <div className="text-3xl font-semibold text-amber-700 mt-1" data-testid="audit-legacy">{audit.summary.LEGACY_DIFFERENCE}</div>
                            </div>
                            <div>
                                <div className="flex items-center gap-2">
                                    <Search className="w-4 h-4 text-rose-600" />
                                    <span className="text-xs text-slate-500 uppercase tracking-wide">{t('diagnostics.audit.investigate')}</span>
                                </div>
                                <div className="text-3xl font-semibold text-rose-700 mt-1" data-testid="audit-investigate">{audit.summary.INVESTIGATE}</div>
                            </div>
                        </div>
                        {Object.keys(reasonCounts).length ? (
                            <div className="border-t border-slate-100 pt-3">
                                <div className="text-xs text-slate-500 uppercase tracking-wide mb-2">{t('diagnostics.audit.reasonBreakdown')}</div>
                                {Object.entries(reasonCounts).map(([k, n]) => (
                                    <div key={k} className="flex justify-between text-sm py-0.5" data-testid={`audit-reason-${k}`}>
                                        <span className="text-slate-700">{t(`diagnostics.audit.reason.${k}`, k)}</span>
                                        <span className="text-slate-500">{n}</span>
                                    </div>
                                ))}
                            </div>
                        ) : null}
                    </>
                ) : null}
            </Card>
        </div>
    );
};

export default AdminDiagnostics;
